import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarDaysIcon, MapPinIcon, UsersIcon } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { Badge } from '../ui/Badge';
import { chefById } from '../../data/chefs';
import { counterpartById } from '../../data/counterparts';
import { currency, longDate } from '../../utils/format';
import { useLocale } from '../../contexts/LocaleContext';
import type { Booking, BookingStatus } from '../../types/booking';
export const statusTone: Record<BookingStatus, 'neutral' | 'accent' | 'success' | 'danger' | 'primary'> = {
  requested: 'neutral',
  quoted: 'accent',
  confirmed: 'success',
  in_progress: 'primary',
  completed: 'primary',
  cancelled: 'danger',
  declined: 'danger'
};
export const statusLabel: Record<BookingStatus, string> = {
  requested: 'Awaiting quote',
  quoted: 'Quote ready',
  confirmed: 'Confirmed',
  in_progress: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  declined: 'Declined'
};
export function BookingCard({
  booking,
  role
}: {
  booking: Booking;
  role: 'chef' | 'customer';
}) {
  const {
    locale,
    currencyCode
  } = useLocale();
  const chef = chefById(booking.chefId);
  const other = role === 'chef' ? counterpartById(booking.customerId) : chef;
  const to = role === 'chef' ? `/chef/bookings/${booking.id}` : `/app/bookings/${booking.id}`;
  return <li data-id="element-84" data-mp="rlt94q4e43ke">
      <Link to={to} className="flex flex-col gap-4 rounded-xl border border-hairline bg-surface p-4 transition-colors duration-150 ease-out hover:border-primary/40 sm:flex-row sm:items-center" data-id="element-85" data-mp="rm1kueos12u0">
        <Avatar name={other?.name ?? 'Guest'} src={(other as {
        photo?: string;
      })?.photo} size="md" data-id="element-86" data-mp="11jpjvgtf0whz" />
        <div className="min-w-0 flex-1" data-id="element-87" data-mp="16jbt7f64hsli">
          <div className="flex flex-wrap items-center gap-2" data-id="element-88" data-mp="16gzgphj4wafu">
            <span className="font-display text-base font-semibold text-ink" data-id="element-89" data-mp="33cdonulpjrzv">{other?.name}</span>
            <Badge tone={statusTone[booking.status]} data-id="element-90" data-mp="38bgkqpjpmnvp">{statusLabel[booking.status]}</Badge>
            <span className="tnum text-[12px] text-ink-3" data-id="element-91" data-mp="308xtx0os3brt">{booking.ref}</span>
          </div>
          <p className="mt-1 text-sm text-ink-2" data-id="element-92" data-mp="2vb60214kpfkz">{booking.menuName ?? `${booking.occasion} · menu not chosen yet`}</p>
          <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-3" data-id="element-93" data-mp="3a8ydmnqk7twj">
            <li className="flex items-center gap-1.5" data-id="element-94" data-mp="358kgw1ues6dt">
              <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-95" data-mp="35b6ou2l7onwk" />
              {longDate(booking.date, locale)} at {booking.time}
            </li>
            <li className="flex items-center gap-1.5" data-id="element-96" data-mp="3e8qs1h2mwu9t">
              <UsersIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-97" data-mp="3syerecr99iuw" />
              {booking.guests} guests
            </li>
            <li className="flex items-center gap-1.5" data-id="element-98" data-mp="3nz1v71ehuzfn">
              <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-99" data-mp="3o1eky0qhue39" />
              {booking.fulfillment === 'in_home' ? 'At the guest’s place' : 'Delivered'}
            </li>
          </ul>
        </div>
        <div className="shrink-0 text-left sm:text-right" data-id="element-100" data-mp="1qadgjl1h1n4">
          <span className="tnum block font-display text-lg font-semibold text-ink" data-id="element-101" data-mp="khp7a5uawlcb">
            {booking.total ? currency(booking.total, locale, currencyCode) : '—'}
          </span>
          <span className="block text-[12px] text-ink-3" data-id="element-102" data-mp="bgiry03e1gkn">{booking.total ? 'quoted total' : 'no quote yet'}</span>
        </div>
      </Link>
    </li>;
}